import React, { useEffect, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector, RootState } from '../../store';
import { useLicenseGate } from '../../hooks/useLicenseGate';
import { isPathHiddenInCurrentEnv } from '../../config/defaultMenu';
import { fetchDashboardSummary } from '../../store/actions/DashboardActions';
import Header, { Action } from '../shared/TailwindComponents/Header';
import DashboardSkeleton from './DashboardSkeleton';
import Badge from '../shared/TailwindComponents/Badge';
import GreetingBar from './sections/GreetingBar';
import QuickMetrics from './sections/QuickMetrics';
import MyProjects from './sections/MyProjects';
import ProcessingItems from './sections/ProcessingItems';
import RecentMatters from './sections/RecentMatters';
import RecentTasks from './sections/RecentTasks';
import ToolsGrid from './sections/ToolsGrid';
import UpcomingEvents from './sections/UpcomingEvents';
import RecentActivityFeed from './sections/RecentActivityFeed';
import {
  PiArrowClockwiseLight,
  PiChartBarLight,
} from 'react-icons/pi';
import { FeatureTour, dashboardOrientationSteps } from '../shared/FeatureTour';
import { useFeatureTour } from '../../hooks/useFeatureTour';

const HomePage: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const history = useHistory();
  const { hasSectionAccess } = useLicenseGate();

  const {
    loading,
    error,
    overview,
    userProjects,
    urgentTasks,
    urgentMatters,
    recentActivity,
    processingItems,
    upcomingEvents,
  } = useAppSelector((state: RootState) => state.dashboard);

  // Always refresh on mount — the dashboard is a live summary of platform state
  // and must reflect any actions taken elsewhere (tasks, matters, projects, etc.)
  // since the user last viewed it. This fires on every navigation into the page.
  useEffect(() => {
    dispatch(fetchDashboardSummary() as any);
  }, [dispatch]);

  const headerActions: Action[] = useMemo(() => {
    const actions: Action[] = [];

    if (hasSectionAccess('analytics') && !isPathHiddenInCurrentEnv('/analytics')) {
      actions.push({
        label: t('Analytics'),
        icon: <PiChartBarLight className="w-4 h-4" />,
        onClick: () => history.push('/analytics'),
        variant: 'outline',
      });
    }

    actions.push({
      label: t('Refresh'),
      icon: <PiArrowClockwiseLight className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />,
      onClick: () => dispatch(fetchDashboardSummary() as any),
      variant: 'neutral',
      disabled: loading,
    });

    return actions;
  }, [t, hasSectionAccess, history, dispatch, loading]);

  const breadcrumbs = useMemo(() => [
    { name: t('Home'), href: '/home', current: true },
  ], [t]);

  const {
    isRunning: isDashboardTourRunning,
    handleJoyrideCallback: handleDashboardTourCallback,
  } = useFeatureTour({
    tourId: 'dashboard-orientation',
    steps: dashboardOrientationSteps,
    readyToShow: !loading && !!overview,
    autoStartDelay: 800,
  });

  if (loading && !overview) {
    return (
      <div className="flex flex-col h-full">
        <Header breadcrumbs={breadcrumbs} actions={headerActions} />
        <DashboardSkeleton />
      </div>
    );
  }

  if (error && !overview) {
    return (
      <div className="flex flex-col h-full">
        <Header breadcrumbs={breadcrumbs} actions={headerActions} />
        <div className="flex-1 flex flex-col items-center justify-center gap-4">
          <Badge color="red">{t('Failed to load dashboard')}</Badge>
          <p className="text-sm text-gray-500 dark:text-gray-400">{error}</p>
          <button
            className="text-sm text-blue-600 dark:text-blue-400 hover:underline"
            onClick={() => dispatch(fetchDashboardSummary() as any)}
          >
            {t('Retry')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <Header breadcrumbs={breadcrumbs} actions={headerActions} />

      <div className="flex-1 overflow-auto custom-scrollbar-main">
        <div className="mx-auto max-w-7xl space-y-4 p-4 lg:p-6">

          {/* Row 1: Greeting + Quick Metrics */}
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="lg:w-2/5" data-tour-id="dashboard-greeting">
              <GreetingBar />
            </div>
            <div className="lg:w-3/5" data-tour-id="dashboard-metrics">
              <QuickMetrics overview={overview} />
            </div>
          </div>

          {/* Row 2: My Projects */}
          <div data-tour-id="dashboard-projects">
            <MyProjects projects={userProjects} loading={loading && userProjects.length === 0} />
          </div>

          {/* Row 2b: Currently Processing */}
          {processingItems.length > 0 && (
            <ProcessingItems items={processingItems} />
          )}

          {/* Row 3: Recently Worked On Matters */}
          <div data-tour-id="dashboard-matters">
            <RecentMatters matters={urgentMatters} loading={loading && urgentMatters.length === 0} />
          </div>

          {/* Row 4: Upcoming Tasks */}
          <div data-tour-id="dashboard-tasks">
            <RecentTasks tasks={urgentTasks} loading={loading && urgentTasks.length === 0} />
          </div>

          {/* Row 5: Tools */}
          <div data-tour-id="dashboard-tools">
            <ToolsGrid />
          </div>

          {/* Row 6: Activity Timeline + Upcoming Events */}
          {upcomingEvents.length > 0 ? (
            <div className="flex flex-col lg:flex-row gap-4">
              <div className="lg:w-1/2" data-tour-id="dashboard-activity">
                <RecentActivityFeed activities={recentActivity} />
              </div>
              <div className="lg:w-1/2">
                <UpcomingEvents events={upcomingEvents} />
              </div>
            </div>
          ) : (
            <div data-tour-id="dashboard-activity">
              <RecentActivityFeed activities={recentActivity} />
            </div>
          )}

        </div>
      </div>

      <FeatureTour
        steps={dashboardOrientationSteps}
        run={isDashboardTourRunning}
        onCallback={handleDashboardTourCallback}
        continuous
      />
    </div>
  );
};

export default HomePage;
